import type { ReactNode } from "react";
import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { interpolate, joinClassNames, localeStorageKey, localeToIntlLocale, resolveInitialLocale, translateMessage, type Locale, type TranslationParams } from "./i18n-core.js";
export type { Locale } from "./i18n-core.js";

type I18nContextValue = {
  intlLocale: string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: TranslationParams) => string;
};

const I18nContext = createContext<I18nContextValue>({
  intlLocale: "en-US",
  locale: "en",
  setLocale: () => {},
  t: (key, params) => interpolate(key, params),
});

export const I18nConsumer = I18nContext.Consumer;

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale);

  const setLocale = useCallback((nextLocale: Locale) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(localeStorageKey, nextLocale);
    }
    setLocaleState(nextLocale);
  }, []);

  const t = useCallback((key: string, params?: TranslationParams) => translateMessage(locale, key, params), [locale]);
  const intlLocale = localeToIntlLocale(locale);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.lang = locale === "zh" ? "zh-Hans" : "en";
    document.title = t("Attenda");
    const description = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (description) {
      description.content = t(
        "Attenda is an AI receptionist platform for New Zealand and Australian service businesses that need calls answered, appointments booked, and customers followed up.",
      );
    }
  }, [locale, t]);

  const value = useMemo<I18nContextValue>(() => ({
    intlLocale,
    locale,
    setLocale,
    t,
  }), [intlLocale, locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return use(I18nContext);
}

export function LanguageSwitcher({
  className,
  tone = "light",
}: {
  className?: string;
  tone?: "light" | "dark" | "plain";
}) {
  const { locale, setLocale, t } = useI18n();
  const languageOptions = [
    { label: t("English"), locale: "en" as const },
    { label: t("Simplified Chinese"), locale: "zh" as const },
  ];
  const activeLanguageLabel = languageOptions.find((option) => option.locale === locale)?.label ?? t("English");

  return (
    <details
      aria-label={t("Language")}
      className={joinClassNames("language-switcher", className)}
      data-tone={tone}
    >
      <summary className="language-switcher-trigger">
        <span>{activeLanguageLabel}</span>
        <ChevronDown aria-hidden="true" />
      </summary>
      <div className="language-switcher-menu" role="menu">
        {languageOptions.map((option) => {
          const selected = option.locale === locale;

          return (
            <button
              aria-checked={selected}
              aria-label={option.locale === "en" ? t("Switch to English") : t("Switch to Chinese")}
              className={joinClassNames("language-switcher-option", selected && "is-active")}
              key={option.locale}
              onClick={(event) => {
                setLocale(option.locale);
                event.currentTarget.closest("details")?.removeAttribute("open");
              }}
              role="menuitemradio"
              type="button"
            >
              <span>{option.label}</span>
              {selected ? <Check aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>
    </details>
  );
}
